/**
 * Plan — field-level effects.
 *
 * Migrated from: plan/plan-base.coffee
 *
 * Old validators → new effects:
 *   CopyPBMPhone              → CopyPBMPhone
 *   CopySPBMPhone             → CopySPBMPhone
 *   CopyPrimaryCarrierPhone   → CopyPrimaryCarrierPhone
 *   CopySecondaryCarrierPhone → CopySecondaryCarrierPhone
 *   CopyVendorPhone           → CopyVendorPhone
 *   CopyBrokerPhone           → CopyBrokerPhone
 *   CopySubBrokerPhone        → CopySubBrokerPhone
 *   CopyPlanGoLiveDate        → CopyPlanGoLiveDate
 *   DisableExistingRows       → DisableExistingRows
 *   ValidatePlanStatus        → ValidatePlanStatus
 *
 * Skipped:
 *   handle(dfd) init logic — update_pbm_bin_pcn, disable_sales_rep_field
 *   update_pbm_bin_pcn — read-mode DOM manipulation
 *   disable_sales_rep_field — role-based field disabling, handle via field permissions
 *
 * Note: CopyPBMPhone, CopySPBMPhone, CopyPrimaryCarrierPhone,
 * CopySecondaryCarrierPhone, CopyBrokerPhone, CopySubBrokerPhone target
 * fields (primary_pbm_id, etc.) that existed on the old plan form but have
 * been restructured into sub-entities (plan_pbm, plan_medical_carrier) in
 * the new system. Their functionality is now covered by CopyPBMInfo,
 * CopySPBMInfo, CopyCarrierInfo on those sub-entities.
 * CopyVendorPhone remains active on the plan form's vendor_id field.
 */
import dayjs from "dayjs";

import { copyRelatedFields } from "@neu/libui";
import type { FieldHandler } from "@neu/libui";

// ─── CopyPBMPhone ──────────────────────────────────────────────────────────
// Old: DSLFx.Validators.CopyPBMPhone
// Trigger field: primary_pbm_id (no longer on plan — see note above)
export const CopyPBMPhone = copyRelatedFields("pbm", {
	phone: "pbm_phone",
	fax: "pbm_fax",
});

// ─── CopySPBMPhone ─────────────────────────────────────────────────────────
// Old: DSLFx.Validators.CopySPBMPhone
// Trigger field: speciality_pbm_id (no longer on plan — see note above)
export const CopySPBMPhone = copyRelatedFields("pbm", {
	phone: "speciality_pbm_phone",
	fax: "speciality_pbm_fax",
});

// ─── CopyPrimaryCarrierPhone ───────────────────────────────────────────────
// Old: DSLFx.Validators.CopyPrimaryCarrierPhone
// Trigger field: primary_carrier_id (no longer on plan — see note above)
export const CopyPrimaryCarrierPhone = copyRelatedFields("carrier", {
	phone: "primary_carrier_phone",
	fax: "primary_carrier_fax",
});

// ─── CopySecondaryCarrierPhone ─────────────────────────────────────────────
// Old: DSLFx.Validators.CopySecondaryCarrierPhone
// Trigger field: secondary_carrier_id (no longer on plan — see note above)
export const CopySecondaryCarrierPhone = copyRelatedFields("carrier", {
	phone: "secondary_carrier_phone",
	fax: "secondary_carrier_fax",
});

// ─── CopyVendorPhone ───────────────────────────────────────────────────────
// Old: DSLFx.Validators.CopyVendorPhone
// Trigger field: vendor_id
// Fetches vendor and copies phone/fax into plan form fields.
export const CopyVendorPhone = copyRelatedFields("vendor", {
	phone: "vendor_phone",
	fax: "vendor_fax",
});

// ─── CopyBrokerPhone ───────────────────────────────────────────────────────
// Old: DSLFx.Validators.CopyBrokerPhone
// Trigger field: broker_id (no longer on plan — see note above)
export const CopyBrokerPhone = copyRelatedFields("broker", {
	phone: "broker_phone",
	fax: "broker_fax",
});

// ─── CopySubBrokerPhone ────────────────────────────────────────────────────
// Old: DSLFx.Validators.CopySubBrokerPhone
// Trigger field: subbroker_id (no longer on plan — see note above)
export const CopySubBrokerPhone = copyRelatedFields("broker", {
	phone: "subbroker_phone",
	fax: "subbroker_fax",
});

// ─── CopyPlanGoLiveDate ────────────────────────────────────────────────────
// Old: DSLFx.Validators.CopyPlanGoLiveDate
// Trigger field: golive_date
// Copies the go-live date to pbm_date_from and primary_carrier_golive
// if those fields are currently empty.
export const CopyPlanGoLiveDate: FieldHandler = (field, { form }) => {
	const golive = field.value;
	if (!golive) return "";

	const pbmDateFrom = form.getFieldState("pbm_date_from")?.value;
	if (!pbmDateFrom) {
		form.setFieldState("pbm_date_from", (s) => {
			s.value = golive;
		});
	}

	const carrierGolive = form.getFieldState("primary_carrier_golive")?.value;
	if (!carrierGolive) {
		form.setFieldState("primary_carrier_golive", (s) => {
			s.value = golive;
		});
	}

	return "";
};

// ─── DisableExistingRows ───────────────────────────────────────────────────
// Old: DSLFx.Validators.DisableExistingRows
// Trigger field: pricing_history (or other jsonb/entity sub-tables)
// In edit mode, disables the sub-table field to prevent editing existing rows.
export const DisableExistingRows: FieldHandler = (field, { formMode }) => {
	if (formMode !== "edit") return "";

	//COMPLICATED-CHECK-FUNCTIONALITY
	// Old system disabled individual row inputs and drag handles via jQuery DOM.
	// In the new system, we disable the entire field. The EntityTable component
	// should respect this to prevent editing of existing rows.
	field.disabled = true;

	return "";
};

// ─── ValidatePlanStatus ────────────────────────────────────────────────────
// Old: DSLFx.Validators.ValidatePlanStatus
// Trigger field: status, golive_date
// If status is not Pending or Terminated and golive_date is in the future,
// forces status to "Pending" and shows a warning.
export const ValidatePlanStatus: FieldHandler = (field, { form }) => {
	const status = form.getFieldState("status")?.value;
	if (status === "Pending" || status === "Account Terminated") return "";

	const goliveDate = form.getFieldState("golive_date")?.value;
	if (!goliveDate) return "";

	const today = dayjs().format("YYYY-MM-DD");
	const liveDate = dayjs(goliveDate).format("YYYY-MM-DD");

	if (dayjs(today).isBefore(dayjs(liveDate))) {
		form.setFieldState("status", (s) => {
			s.value = "Pending";
		});
		field.setFeedback({
			type: "warning",
			messages: [
				'Plan status will be "Pending" because it is not live yet.',
			],
		});
	}

	return "";
};
